import { Img } from "@/components/site/Img";
import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Phone, MessageSquareText } from "lucide-react";
import { PHONE_HREF, PHONE_DISPLAY, SMS_DRAFTS } from "@/lib/contact";
import { useSmsHref } from "@/components/site/ContactButtons";
import { track } from "@/lib/track";

const API = process.env.REACT_APP_BACKEND_URL;
const GREETING =
  `Hi, I'm Stone. I can answer quick questions about pieces, finishes, and repairs. For pricing, availability, or to send a repair photo, call Chris first or text him at ${PHONE_DISPLAY}. What are you looking for?`;
const FALLBACK =
  `Sorry, I'm having trouble right now. Call Chris at ${PHONE_DISPLAY}, or text him at the same number, and he'll help you directly.`;

const StoneContactRow = () => {
  const smsHref = useSmsHref(SMS_DRAFTS.general);
  const btn =
    "flex items-center justify-center gap-2 min-h-[44px] px-2 py-2 text-[10px] tracking-[0.14em] uppercase transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7A6547]";
  return (
    <div
      data-testid="chat-contact-row"
      className="grid grid-cols-2 gap-2 px-3 py-2.5 border-b border-[#D5CEBD] bg-[#F3EDDF]"
    >
      <a
        href={PHONE_HREF}
        onClick={() => track("call_click", "Stone chat")}
        data-testid="chat-call-chris"
        aria-label={`Call Chris at ${PHONE_DISPLAY}`}
        className={`${btn} bg-[#7A6547] text-[#F9F6F0] hover:bg-[#5E4D35]`}
      >
        <Phone size={14} strokeWidth={1.5} aria-hidden="true" />
        <span className="flex flex-col items-start leading-tight">
          <span>Call Chris</span>
          <span data-testid="chat-call-number" className="text-[10px] tracking-[0.04em] normal-case text-white/90">{PHONE_DISPLAY}</span>
        </span>
      </a>
      <a
        href={smsHref}
        onClick={() => track("text_click", "Stone chat")}
        data-testid="chat-text-chris"
        aria-label={`Text Chris at ${PHONE_DISPLAY}`}
        title={`Text ${PHONE_DISPLAY}`}
        className={`${btn} border border-[#7A6547] text-[#7A6547] hover:bg-[#7A6547] hover:text-[#F9F6F0]`}
      >
        <MessageSquareText size={14} strokeWidth={1.5} aria-hidden="true" />
        <span className="flex flex-col items-start leading-tight">
          <span>Text Chris</span>
          <span className="text-[10px] tracking-[0.04em] normal-case opacity-90">Send a photo</span>
        </span>
      </a>
    </div>
  );
};


function getSessionId() {
  let id = sessionStorage.getItem("gfys-chat-session");
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("gfys-chat-session", id);
  }
  return id;
}

const ProductCard = ({ card, onAsk }) => (
  <div
    data-testid={`chat-product-card-${card.id}`}
    className="w-[85%] shrink-0 self-start bg-white border border-[#D5CEBD] overflow-hidden"
  >
    <Img src={card.image} alt={card.name} loading="lazy" className="w-full h-44 object-contain bg-[#EFEBE1]" />
    <div className="p-3">
      <div className="font-serif text-[15px] text-[#2C2A28] leading-tight">{card.name}</div>
      <div className="text-[10px] text-[#6F6A60] mt-1 uppercase tracking-[0.16em]">
        {card.finish || card.category}
        {card.size ? ` · ${card.size}` : ""}
      </div>
      {card.price_display && (
        <div className="text-sm text-[#7A6547] font-medium mt-1">{card.price_display}</div>
      )}
      <button
        data-testid={`chat-card-ask-${card.id}`}
        onClick={() => onAsk(card)}
        className="mt-2.5 w-full px-3 py-2 bg-[#1F1E1C] text-[#C8B387] text-[10px] tracking-[0.16em] uppercase hover:bg-[#7A6547] hover:text-white transition-colors"
      >
        {card.button_label}
      </button>
    </div>
  </div>
);

const ChatMessage = ({ msg }) => (
  <div
    data-testid={msg.role === "user" ? "chat-message-user" : "chat-message-bot"}
    className={`max-w-[85%] shrink-0 px-4 py-3 text-sm leading-relaxed ${
      msg.role === "user"
        ? "self-end bg-[#7A6547] text-[#F9F6F0]"
        : "self-start bg-white border border-[#D5CEBD] text-[#2C2A28]"
    }`}
  >
    {msg.content}
  </div>
);

const TypingDots = () => (
  <div
    data-testid="chat-typing-indicator"
    className="self-start shrink-0 bg-white border border-[#D5CEBD] px-4 py-3 flex gap-1.5 items-center"
  >
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-[#7A6547] animate-bounce"
        style={{ animationDelay: `${i * 150}ms` }}
      />
    ))}
  </div>
);

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const last = messages[messages.length - 1];
    if (last?.role === "assistant" && messages.length > 1) {
      const bots = container.querySelectorAll('[data-testid="chat-message-bot"]');
      const el = bots[bots.length - 1];
      if (el) {
        container.scrollTop =
          el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 8;
        return;
      }
    }
    container.scrollTop = container.scrollHeight;
  }, [messages, sending, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const openChat = () => setOpen(true);
    window.addEventListener("gfys-open-chat", openChat);
    return () => window.removeEventListener("gfys-open-chat", openChat);
  }, []);

  const sendMessage = useCallback(async (textArg) => {
    const text = (typeof textArg === "string" ? textArg : input).trim();
    if (!text || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setSending(true);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply, cards: data.cards || [] },
      ]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: FALLBACK }]);
    } finally {
      setSending(false);
    }
  }, [input, sending]);

  return (
    <>
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-[58px] md:bottom-[140px] right-4 md:right-6 z-[1100] w-[min(92vw,380px)] flex flex-col bg-[#F9F6F0] border border-[#D5CEBD] shadow-[0_18px_48px_-18px_rgba(31,30,28,0.5)] fade-up"
        >
          <div className="flex items-center justify-between bg-[#1F1E1C] px-5 py-4 border-b border-[#C8B387]/30">
            <div>
              <div className="font-serif text-lg text-[#F9F6F0] font-light leading-tight">
                Ask Stone
              </div>
              <div className="text-[9px] tracking-[0.28em] uppercase text-[#C8B387] mt-1">
                Garden Fountains Yard Sculptures
              </div>
            </div>
            <button
              data-testid="chat-widget-close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="w-8 h-8 border border-white/30 text-white/80 flex items-center justify-center hover:bg-white hover:text-[#1F1E1C] transition-colors"
            >
              <X size={14} strokeWidth={1.5} />
            </button>
          </div>

          <StoneContactRow />

          <div
            ref={scrollRef}
            data-testid="chat-messages"
            className="flex flex-col gap-3 px-4 py-4 h-[340px] max-h-[45vh] overflow-y-auto"
          >
            {messages.map((msg, i) => (
              <div key={i} className="contents">
                <ChatMessage msg={msg} />
                {msg.cards?.map((card) => (
                  <ProductCard key={`${i}-${card.id}`} card={card} onAsk={(c) => sendMessage(c.ask_message)} />
                ))}
              </div>
            ))}
            {sending && <TypingDots />}
          </div>

          <div className="flex items-center gap-2 border-t border-[#D5CEBD] bg-white px-3 py-3">
            <input
              ref={inputRef}
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Ask about sizes, colors, delivery..."
              maxLength={1000}
              className="flex-1 bg-transparent text-sm text-[#2C2A28] placeholder-[#8A857B] outline-none px-2 py-2"
            />
            <button
              data-testid="chat-send-button"
              onClick={sendMessage}
              disabled={sending || !input.trim()}
              aria-label="Send message"
              className="w-10 h-10 bg-[#7A6547] text-[#F9F6F0] flex items-center justify-center hover:bg-[#5E4D35] transition-colors disabled:opacity-40"
            >
              <Send size={15} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}


      <button
        data-testid="chat-widget-button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat" : "Open chat — ask about a piece"}
        className={`hidden md:flex fixed md:bottom-[72px] md:right-6 z-[1100] h-14 ${open ? "w-14 justify-center" : "pl-4 pr-5 gap-2.5"} rounded-full bg-[#7A6547] border-2 border-white/60 text-[#F9F6F0] items-center shadow-[0_10px_30px_-8px_rgba(31,30,28,0.6)] hover:bg-[#1F1E1C] hover:text-[#C8B387] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C8B387] transition-colors ${!open ? "chat-pulse" : ""}`}
      >
        {open ? (
          <X size={20} strokeWidth={1.5} />
        ) : (
          <>
            <MessageCircle size={20} strokeWidth={1.5} />
            <span data-testid="chat-widget-label" className="text-[12px] tracking-[0.2em] uppercase font-medium">Ask Stone</span>
          </>
        )}
      </button>
    </>
  );
}
